import { useState, useCallback } from 'react';
import { Skill } from '../types';

interface ComboState {
  lastSkillUsed: string | null;
  lastSkillTime: number;
  comboCount: number;
  availableNextSkills: string[];
}

export const useComboSystem = () => {
  const [comboState, setComboState] = useState<ComboState>({
    lastSkillUsed: null,
    lastSkillTime: 0,
    comboCount: 0,
    availableNextSkills: []
  });

  // 检查技能是否可以连击
  const canCombo = useCallback((skill: Skill, currentTime: number): boolean => {
    if (!skill.combo || !skill.combo.fromSkills || skill.combo.fromSkills.length === 0) {
      return false;
    }

    // 检查连击窗口时间（默认30秒）
    const comboWindow = skill.combo.comboWindow || 30;
    const timeSinceLastSkill = (currentTime - comboState.lastSkillTime) / 1000;
    
    if (timeSinceLastSkill > comboWindow) {
      return false;
    }

    // 检查是否从正确的前置技能连击
    return skill.combo.fromSkills.includes(comboState.lastSkillUsed || '');
  }, [comboState]);


  // 检查技能是否应该高亮（表示可以连击）
  const shouldHighlight = useCallback((skill: Skill, currentTime: number): boolean => {
    return canCombo(skill, currentTime);
  }, [canCombo]);

  // 获取当前可用的连击技能列表
  const getAvailableComboSkills = useCallback((allSkills: Skill[], currentTime: number): string[] => {
    if (!comboState.lastSkillUsed) return [];

    // 找到上一个使用的技能
    const lastSkill = allSkills.find(s => s.id === comboState.lastSkillUsed);
    if (!lastSkill?.combo?.toSkills) return [];

    // 检查连击窗口
    const comboWindow = 30; // 默认30秒连击窗口
    const timeSinceLastSkill = (currentTime - comboState.lastSkillTime) / 1000;
    
    if (timeSinceLastSkill > comboWindow) {
      return [];
    }

    return lastSkill.combo.toSkills;
  }, [comboState]);

  // 使用技能时更新连击状态
  const useSkill = useCallback((skill: Skill, allSkills: Skill[]) => {
    const currentTime = Date.now();
    const wasCombo = canCombo(skill, currentTime);
    
    // 更新连击状态
    setComboState(prev => {
      // 如果不是连击技能但有前置连击状态，检查是否打断了连击
      if (!wasCombo && prev.lastSkillUsed && prev.availableNextSkills.length > 0) {
        // 如果当前技能不在可连击列表中，则重置连击状态
        if (!prev.availableNextSkills.includes(skill.id)) {
          console.log(`连击被打断！使用了 ${skill.name} 而不是预期的连击技能`);
          // 重置连击状态，但仍然设置当前技能为新的起始点
          return {
            lastSkillUsed: skill.id,
            lastSkillTime: currentTime,
            comboCount: 1,
            availableNextSkills: skill.combo?.toSkills || []
          };
        }
      }
      
      const newComboCount = wasCombo ? prev.comboCount + 1 : 1;
      
      // 获取下一步可以连击的技能
      const nextAvailableSkills = skill.combo?.toSkills || [];
      
      return {
        lastSkillUsed: skill.id,
        lastSkillTime: currentTime,
        comboCount: newComboCount,
        availableNextSkills: nextAvailableSkills
      };
    });

    // 返回连击信息
    return {
      wasCombo,
      comboCount: wasCombo ? comboState.comboCount + 1 : 1
    };
  }, [canCombo, comboState.comboCount]);

  // 重置连击状态
  const resetCombo = useCallback(() => {
    setComboState({
      lastSkillUsed: null,
      lastSkillTime: 0,
      comboCount: 0,
      availableNextSkills: []
    });
  }, []);

  // 检查连击是否已过期
  const isComboExpired = useCallback((currentTime: number): boolean => {
    if (!comboState.lastSkillUsed) return false;
    
    const timeSinceLastSkill = (currentTime - comboState.lastSkillTime) / 1000;
    return timeSinceLastSkill > 30; // 默认30秒连击窗口，可通过技能配置调整
  }, [comboState]);

  return {
    comboState,
    canCombo,
    shouldHighlight,
    getAvailableComboSkills,
    useSkill,
    resetCombo,
    isComboExpired
  };
};